import UtilityService from "./UtilityService";
import { postToAgents } from "./PlaybookApi";

const SKILL_REVIEW_LABELS = {
	additive: { label: "Checked: adds to the skill without changing what it does", tone: "success" },
	changes_what_it_does: { label: "This changes what the skill does", tone: "warning" },
	breaks: { label: "This would break the skill", tone: "danger" },
	not_reviewed: { label: "The check could not run", tone: "warning" },
};

const SkillLibraryService = {
	/**
	 * The firm's skills and the built-in ones, each with its tools and how many agents use it.
	 *
	 * @return {Promise<Array>} [{ id, name, description, versionNumber, isBuiltin, toolNames, createdBy, updatedAt, usedByCount }]
	 */
	listSkills() {
		return postToAgents({ operationId: "listPlaybookSkills", body: {} });
	},

	/**
	 * One skill with its full text, its tools, every version, and whether the viewer may edit it.
	 *
	 * @param {Object} params
	 * @param {Number} params.skillId
	 * @return {Promise<Object>}
	 */
	getSkill({ skillId }) {
		return postToAgents({ operationId: "getPlaybookSkill", body: { skillId } });
	},

	/**
	 * Saves a skill: a new one when skillId is absent, otherwise a new version of it credited to
	 * the viewer. Resolves to the skill as getSkill returns it.
	 *
	 * @param {Object} params
	 * @param {Number} [params.skillId]
	 * @param {Number} params.categoryId
	 * @param {String} params.name
	 * @param {String} params.description
	 * @param {String} params.body
	 * @param {Array<String>} params.toolNames
	 * @param {String} params.changeNote
	 * @param {Object} [params.authorization] { reason } to save a change the Skill updater did not approve
	 * @return {Promise<Object>} the skill, or { needsAuthorization, review, playbooksUsing }
	 */
	saveSkill({ skillId, categoryId, name, description, body, toolNames, reads, changeNote, authorization }) {
		return postToAgents({
			operationId: "savePlaybookSkill",
			body: { skillId, categoryId, name, description, body, toolNames, reads, changeNote, authorization },
		});
	},

	/**
	 * What the Skill updater's verdict means to the person saving, as a short label and a tone.
	 *
	 * @param {Object} params
	 * @param {Object} params.review { verdict }
	 * @return {Object} { label, tone }
	 */
	describeSkillReview({ review }) {
		const described = SKILL_REVIEW_LABELS[review.verdict] || SKILL_REVIEW_LABELS.not_reviewed;
		return described;
	},

	/**
	 * Takes a firm skill out of the Skills list.
	 *
	 * @param {Object} params
	 * @param {Number} params.skillId
	 * @return {Promise<Object>}
	 */
	archiveSkill({ skillId }) {
		return postToAgents({ operationId: "archivePlaybookSkill", body: { skillId } });
	},

	/**
	 * A skill's tools as the catalogue names them. A tool the catalogue no longer has keeps its
	 * stored name and is marked unknown.
	 *
	 * @param {Object} params
	 * @param {Array<String>} params.toolNames
	 * @param {Array} params.catalogue
	 * @return {Array} [{ name, label, needs, isKnown }]
	 */
	toolsNamed({ toolNames, catalogue }) {
		const tools = (toolNames || []).map(name => {
			const tool = (catalogue || []).find(candidate => candidate.name === name);
			if (!tool) return { name, label: name, needs: null, isKnown: false };
			return { name, label: tool.label, needs: tool.needs, isKnown: true };
		});
		return tools;
	},

	/**
	 * How many agents use a skill, in words.
	 *
	 * @param {Object} params
	 * @param {Number} params.usedByCount
	 * @return {String}
	 */
	describeSkillUsage({ usedByCount }) {
		const count = Number(usedByCount) || 0;
		if (count === 0) return "Not used by any playbook yet";
		const usage = `Used by ${UtilityService.describeCount({ count, singular: "playbook" })}`;
		return usage;
	},

	/**
	 * The fields a skill still needs before it can be saved, by their labels. A new version of a
	 * skill that already exists needs a note saying why; moving it to another category does not.
	 *
	 * @param {Object} params
	 * @param {Object} params.draft { categoryId, name, body, changeNote }
	 * @param {Boolean} params.isNew
	 * @param {Boolean} [params.isVersionChanged] whether the save writes a new version
	 * @return {Array<String>}
	 */
	missingSkillFields({ draft, isNew, isVersionChanged = true }) {
		const missing = [];
		if (!draft.categoryId) missing.push("Category");
		if (!String(draft.name || "").trim()) missing.push("Name");
		if (!String(draft.body || "").trim()) missing.push("Instructions");
		if (!isNew && isVersionChanged && !String(draft.changeNote || "").trim()) missing.push("What changed and why");
		return missing;
	},

	/**
	 * Whether saving a skill draft writes a new version: what the person typed for its name,
	 * description or text, or the tools or what it reads up front, differ from the skill as saved.
	 * The category is filing, not part of a version.
	 *
	 * @param {Object} params
	 * @param {Object} params.draft { name, description, body, toolNames, reads }
	 * @param {Object} params.skill the skill as getSkill returns it
	 * @return {Boolean}
	 */
	isSkillVersionChanged({ draft, skill }) {
		const sortedList = names => [...(names || [])].sort().join(",");
		const readTools = reads => (reads || []).map(read => read.tool);
		const typedText = value => String(value || "").trim();
		const savedText = value => String(value || "");
		const isChanged =
			typedText(draft.name) !== savedText(skill.name) ||
			typedText(draft.description) !== savedText(skill.description) ||
			typedText(draft.body) !== savedText(skill.body) ||
			sortedList(draft.toolNames) !== sortedList(skill.toolNames) ||
			sortedList(readTools(draft.reads)) !== sortedList(readTools(skill.reads));
		return isChanged;
	},

	/**
	 * The skills filed under a category, A to Z by name.
	 *
	 * @param {Object} params
	 * @param {Array} params.skills
	 * @param {Number} params.categoryId
	 * @return {Array}
	 */
	skillsInCategory({ skills, categoryId }) {
		const inCategory = skills.filter(skill => Number(skill.categoryId) === Number(categoryId));
		inCategory.sort((first, second) => first.name.localeCompare(second.name));
		return inCategory;
	},

	/**
	 * The skills in a list that match a search (name or description), a kind (all, built-in or
	 * the firm's own) and, when one is chosen, a tool they use.
	 *
	 * @param {Object} params
	 * @param {Array} params.skills
	 * @param {String} params.search
	 * @param {String} params.kind all | builtin | custom
	 * @param {String} params.toolName "" for any
	 * @return {Array}
	 */
	skillsMatching({ skills, search, kind, toolName }) {
		const searchText = UtilityService.toSearchText(search);
		const matching = skills.filter(skill => {
			const isKind = kind === "all" || (kind === "builtin" ? !!skill.isBuiltin : !skill.isBuiltin);
			if (!isKind) return false;
			const usesTool = !toolName || (skill.toolNames || []).includes(toolName);
			if (!usesTool) return false;
			if (!searchText) return true;
			const haystack = UtilityService.toSearchText(`${skill.name || ""} ${skill.description || ""}`);
			const isMatch = haystack.includes(searchText);
			return isMatch;
		});
		return matching;
	},
};

export default SkillLibraryService;
